import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Menu, X, Bell, ShieldCheck, Copy, Check,
  ClipboardPaste, Lock, ArrowRight, RefreshCw, Diamond, Bitcoin,
} from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { KYC } from '@/constants/testIds';
import { BRAND, BRAND_MONOGRAM, DOMAIN, ADDRESSES } from '@/verificationData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.19, 1, 0.22, 1];

const T = {
  fr: {
    kicker: 'Vérification d\u2019adresse / KYC',
    greet: () => {
      const h = new Date().getHours();
      return h < 12 ? 'Bonjour' : h < 18 ? 'Bon après-midi' : 'Bonsoir';
    },
    greetName: 'Investisseur',
    step: 'ÉTAPE',
    navHome: 'Accueil', navAbout: 'À propos', navPortfolio: 'Portefeuille',
    navContact: 'Contact', navKyc: 'Vérification / KYC',
    s1Title: 'Partenaire de vérification',
    partnerRole: 'Partenaire de vérification',
    s1p1: "AML Crypto est reconnu comme l'un des leaders mondiaux de la conformité crypto et des services de lutte contre le blanchiment, offrant vérification avancée, évaluation du risque et surveillance continue pour renforcer la légitimité et la sécurité des transactions blockchain. Leur approche combine le screening exhaustif des adresses, l'analyse des transactions et la vérification de conformité réglementaire, garantissant que toutes les opérations crypto respectent les standards internationaux AML/KYC.",
    s1p2: "Au-delà de la vérification technique, AML Crypto fournit une évaluation d'intelligence et de légitimité des adresses et transactions crypto, contribuant à protéger le capital engagé et à renforcer la confiance dans l'intégrité de l'écosystème. Avec la vérification AML Crypto, les investisseurs gagnent en transparence, en visibilité du risque et en assurance quant à la légitimité de leurs opérations.",
    s2Title: 'Adresses de vérification',
    s2Note: 'Utilisez les adresses officielles ci-dessous pour compléter votre vérification.',
    badge: 'Sécurisé',
    copy: 'Copier', copied: 'Copié',
    labels: { btc: 'Bitcoin (BTC)', eth: 'Ethereum (ETH)', trx: 'Tron (TRX)' },
    statusTitle: 'Statut de votre vérification',
    statusRef: 'Référence',
    statusDate: 'Soumis le',
    refresh: 'Actualiser',
    statuses: { pending: 'En attente', verified: 'Validée', rejected: 'Rejetée' },
    s3Title: 'Vérification d\u2019adresse / KYC',
    s3Sub: 'Soumettez votre adresse crypto pour vérification.',
    fName: 'Nom', fNamePh: 'Nom légal complet',
    fRef: 'N°', fRefPh: 'Numéro de référence (optionnel)',
    fAddr: 'Adresse crypto', fAddrPh: 'Collez l\u2019adresse ici',
    paste: 'Coller',
    encrypt: 'Tous les documents sont chiffrés et traités conformément aux réglementations sur la protection des données.',
    submit: 'Soumettre la vérification',
    submitting: 'Envoi…',
    renewTitle: 'Renouveler / Réessayer une vérification',
    renew: 'Renouveler · Réessayer',
    renewSubtitle: 'Une nouvelle vérification sera envoyée.',
    renewSent: 'Adresse envoyée pour vérification.',
    renewErr: 'L\u2019envoi a échoué. Merci de réessayer.',
    renewEmpty: 'Le presse-papiers est vide.',
    success: 'Vérification soumise. Notre équipe l\u2019examinera sous peu.',
    err: 'Merci d\u2019indiquer votre nom et une adresse.',
    pasteErr: 'Impossible de lire le presse-papiers.',
    copyErr: 'Impossible de copier.',
    private: 'Privée',
  },
  en: {
    kicker: 'Address Verification / KYC',
    greet: () => {
      const h = new Date().getHours();
      return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
    },
    greetName: 'Investor',
    step: 'STEP',
    navHome: 'Home', navAbout: 'About', navPortfolio: 'Portfolio',
    navContact: 'Contact', navKyc: 'Verification / KYC',
    s1Title: 'Verification Partner',
    partnerRole: 'Verification Partner',
    s1p1: 'AML Crypto is recognised as one of the global leaders in cryptocurrency compliance and anti-money laundering services, providing advanced verification, risk assessment, and continuous monitoring to strengthen the legitimacy and security of blockchain transactions. Their approach combines comprehensive address screening, transaction analysis, and regulatory compliance verification, ensuring that all crypto operations meet international AML/KYC standards.',
    s1p2: 'Beyond technical verification, AML Crypto delivers intelligence and legitimacy assessment on crypto addresses and transactions, helping protect the capital involved and reinforcing confidence in the integrity of the ecosystem. With AML Crypto verification, investors gain greater transparency, improved risk visibility, and a higher level of assurance regarding the legitimacy of their crypto operations.',
    s2Title: 'Verification Addresses',
    s2Note: 'Use the official addresses below to complete your verification.',
    badge: 'Secured',
    copy: 'Copy', copied: 'Copied',
    labels: { btc: 'Bitcoin (BTC)', eth: 'Ethereum (ETH)', trx: 'Tron (TRX)' },
    statusTitle: 'Your verification status',
    statusRef: 'Reference',
    statusDate: 'Submitted on',
    refresh: 'Refresh',
    statuses: { pending: 'Pending', verified: 'Verified', rejected: 'Rejected' },
    s3Title: 'Address Verification / KYC',
    s3Sub: 'Submit your crypto address for verification.',
    fName: 'Name', fNamePh: 'Full legal name',
    fRef: 'N°', fRefPh: 'Reference number (optional)',
    fAddr: 'Crypto address', fAddrPh: 'Paste address here',
    paste: 'Paste',
    encrypt: 'All documents are encrypted and processed in compliance with data protection regulations.',
    submit: 'Submit Verification',
    submitting: 'Submitting…',
    renewTitle: 'Renew / Retry a new verification',
    renew: 'Renew · Retry',
    renewSubtitle: 'A new verification will be sent.',
    renewSent: 'Address sent for verification.',
    renewErr: 'Sending failed. Please try again.',
    renewEmpty: 'Your clipboard is empty.',
    success: 'Verification submitted. Our team will review it shortly.',
    err: 'Please provide your name and an address.',
    pasteErr: 'Could not read clipboard.',
    copyErr: 'Could not copy.',
    private: 'Private',
  },
};

const AddrIcon = ({ type }) => {
  if (type === 'eth') return <Diamond size={26} strokeWidth={1.5} className="text-foreground" />;
  if (type === 'trx') return <span className="font-display text-2xl leading-none text-foreground">T</span>;
  return <Bitcoin size={26} strokeWidth={1.5} className="text-[#0a0b0e]" />;
};

const statusStyle = (s) =>
  s === 'verified'
    ? 'border-emerald-500/50 text-emerald-400'
    : s === 'rejected'
    ? 'border-red-500/60 text-red-400'
    : 'border-primary/50 text-primary';

const StepLabel = ({ n, t }) => (
  <div className="inline-flex flex-col items-center justify-center border border-primary/40 px-6 py-4 min-w-[110px]">
    <span className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-muted-foreground">
      {t.step}
    </span>
    <span className="font-mono text-lg tracking-[0.1em] text-primary">{n}</span>
  </div>
);

export default function Verification() {
  const [lang, setLang] = useState('fr');
  const t = T[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [form, setForm] = useState({ name: '', reference: '', address: '' });
  const [loading, setLoading] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [tracked, setTracked] = useState(null);

  useEffect(() => {
    const id = localStorage.getItem('uri_kyc_last_id');
    if (id) fetchStatus(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = async (id) => {
    try {
      const res = await axios.get(`${API}/verification/${id}`);
      setTracked(res.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        localStorage.removeItem('uri_kyc_last_id');
        setTracked(null);
      }
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const copyAddress = async (item) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(item.address);
      } else {
        const ta = document.createElement('textarea');
        ta.value = item.address;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedKey(item.key);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch {
      toast.error(t.copyErr);
    }
  };

  const pasteAddress = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setForm((f) => ({ ...f, address: text }));
    } catch {
      toast.error(t.pasteErr);
    }
  };

  const pasteRenew = async () => {
    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch {
      toast.error(t.pasteErr);
      return;
    }
    const address = (text || '').trim();
    if (!address) {
      toast.error(t.renewEmpty);
      return;
    }
    setRenewLoading(true);
    try {
      const res = await axios.post(`${API}/verification/renew`, { address });
      localStorage.setItem('uri_kyc_last_id', res.data.id);
      setTracked(res.data);
      toast.success(t.renewSent);
    } catch {
      toast.error(t.renewErr);
    } finally {
      setRenewLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim()) {
      toast.error(t.err);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/verification`, form);
      localStorage.setItem('uri_kyc_last_id', res.data.id);
      setTracked(res.data);
      toast.success(t.success);
      setForm({ name: '', reference: '', address: '' });
    } catch {
      toast.error(t.err);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { to: '/', label: t.navHome },
    { to: '/#about', label: t.navAbout },
    { to: '/#portfolio', label: t.navPortfolio },
    { to: '/#contact', label: t.navContact },
  ];

  return (
    <div data-testid={KYC.page} className="App grain min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-[rgba(10,11,14,0.82)] backdrop-blur-xl">
        <div className="mx-auto max-w-screen-lg px-5 md:px-8 py-5 flex items-center gap-4">
          <button
            data-testid={KYC.menuButton}
            onClick={() => setMenuOpen(true)}
            aria-label="Menu"
            className="flex h-12 w-12 shrink-0 items-center justify-center border border-border text-foreground hover:border-primary hover:text-primary transition-colors duration-300"
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>

          <div className="flex-1 min-w-0">
            <div className="overline mb-1 truncate">{t.kicker}</div>
            <h1 className="font-display font-light tracking-tight text-2xl md:text-3xl text-foreground leading-none">
              {t.greet()}, <span className="italic text-primary">{t.greetName}</span>.
            </h1>
          </div>

          <button
            data-testid={KYC.langToggle}
            onClick={() => setLang((l) => (l === 'fr' ? 'en' : 'fr'))}
            className="hidden sm:flex h-12 items-center px-4 border border-border font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-300"
          >
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>

          <button
            data-testid={KYC.bell}
            aria-label="Notifications"
            className="relative flex h-12 w-12 shrink-0 items-center justify-center border border-border text-foreground hover:border-primary hover:text-primary transition-colors duration-300"
          >
            <Bell size={20} strokeWidth={1.5} />
            <span className="absolute top-2.5 right-3 h-2 w-2 rounded-full bg-red-500" />
          </button>
        </div>
      </header>

      {/* Slide-in menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid={KYC.menuOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 bg-[rgba(10,11,14,0.96)] backdrop-blur-xl"
          >
            <div className="mx-auto max-w-screen-lg px-6 md:px-8 py-6 flex items-center justify-between">
              <span className="font-display text-2xl tracking-tight text-foreground">
                Zukunft Real Estate<span className="text-primary"> GmbH</span>
              </span>
              <button
                data-testid={KYC.menuClose}
                onClick={() => setMenuOpen(false)}
                aria-label="Close"
                className="flex h-12 w-12 items-center justify-center border border-border text-foreground hover:border-primary hover:text-primary transition-colors duration-300"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="mx-auto max-w-screen-lg px-6 md:px-8 mt-10 flex flex-col gap-2">
              {menuItems.map((m, i) => (
                <motion.div
                  key={m.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, ease: EASE }}
                >
                  <Link
                    to={m.to}
                    onClick={() => setMenuOpen(false)}
                    className="block font-display font-light text-4xl md:text-5xl tracking-tight text-foreground hover:text-primary transition-colors duration-300 py-2"
                  >
                    {m.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-primary">
                — {t.navKyc}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="mx-auto max-w-screen-lg px-5 md:px-8 pb-28 pt-10">
        {/* STEP 01 — Partner */}
        <section className="pt-6">
          <div className="flex items-start gap-6 mb-10">
            <StepLabel n="01" t={t} />
            <h2 className="font-display font-light tracking-tight text-4xl md:text-5xl text-foreground leading-[1.05] pt-1">
              {t.s1Title}
            </h2>
          </div>

          <Reveal>
            <div className="border border-border bg-[#101216] p-7 md:p-9">
              <div className="flex items-start gap-5 mb-6">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-primary/40 bg-primary/[0.06]">
                  <span className="font-display text-xl tracking-tight text-primary">
                    {BRAND_MONOGRAM}
                  </span>
                </div>
                <h3 className="font-display font-light text-3xl md:text-4xl tracking-tight text-foreground leading-[1.05]">
                  {BRAND}
                </h3>
              </div>

              <div className="flex items-center gap-5 border-t border-border pt-6">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center bg-red-800">
                  <ShieldCheck size={30} strokeWidth={1.5} className="text-foreground" />
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl tracking-tight text-foreground leading-none">
                    AmlCrypto.io
                  </div>
                  <div className="mt-2 overline">{t.partnerRole}</div>
                </div>
              </div>

              <div className="mt-8 space-y-5">
                <p className="font-body font-light leading-relaxed text-muted-foreground">{t.s1p1}</p>
                <p className="font-body font-light leading-relaxed text-muted-foreground">{t.s1p2}</p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* STEP 02 — Addresses */}
        <section className="pt-24">
          <div className="flex items-start gap-6 mb-6">
            <StepLabel n="02" t={t} />
            <h2 className="font-display font-light tracking-tight text-4xl md:text-5xl text-foreground leading-[1.05] pt-1">
              {t.s2Title}
            </h2>
          </div>

          <Reveal>
            <p className="italic font-body font-light text-muted-foreground mb-6">{t.s2Note}</p>
            <div className="inline-flex items-center gap-2 border border-primary/40 px-4 py-2.5 mb-8">
              <Lock size={13} strokeWidth={1.5} className="text-primary" />
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-primary">
                {t.badge}
              </span>
            </div>
          </Reveal>

          <div className="space-y-5">
            {ADDRESSES.map((item, i) => (
              <Reveal key={item.key} delay={i * 0.06}>
                <div className="border border-border bg-[#101216] p-6 md:p-7">
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex h-14 w-14 shrink-0 items-center justify-center border border-border ${
                        item.key === 'btc' ? 'bg-primary' : item.key === 'trx' ? 'bg-red-800' : 'bg-[#26221c]'
                      }`}
                    >
                      <AddrIcon type={item.icon} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-primary mb-2">
                        {t.labels[item.key]}
                      </div>
                      <div className="font-mono text-sm text-foreground break-all leading-relaxed">
                        {item.address}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={KYC.copyButton(item.key)}
                    onClick={() => copyAddress(item)}
                    className="mt-6 w-full flex items-center justify-center gap-2 border border-primary/40 py-3.5 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                  >
                    {copiedKey === item.key ? <Check size={15} strokeWidth={1.5} /> : <Copy size={15} strokeWidth={1.5} />}
                    {copiedKey === item.key ? t.copied : t.copy}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* STEP 03 — Form */}
        <section className="pt-24">
          <div className="flex items-start gap-6 mb-8">
            <StepLabel n="03" t={t} />
            <h2 className="font-display font-light tracking-tight text-4xl md:text-5xl text-foreground leading-[1.05] pt-1">
              {t.s3Title}
            </h2>
          </div>

          {tracked && (
            <Reveal>
              <div
                data-testid={KYC.statusPanel}
                className="mb-8 border border-border bg-[#101216] p-6 md:p-7"
              >
                <div className="flex items-center justify-between gap-4 mb-6">
                  <span className="overline">{t.statusTitle}</span>
                  <button
                    data-testid={KYC.statusRefresh}
                    onClick={() => fetchStatus(tracked.id)}
                    className="flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    <RefreshCw size={13} strokeWidth={1.5} />
                    {t.refresh}
                  </button>
                </div>
                <div className="flex flex-wrap items-end justify-between gap-6">
                  <div className="space-y-3">
                    <div>
                      <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground">
                        {t.statusRef}
                      </div>
                      <div className="font-mono text-sm text-foreground">
                        {tracked.id.slice(0, 8).toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground">
                        {t.statusDate}
                      </div>
                      <div className="font-body text-sm text-foreground/80">
                        {new Date(tracked.created_at).toLocaleDateString(lang, {
                          year: 'numeric', month: 'long', day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                  <div
                    data-testid={KYC.statusBadge}
                    className={`inline-flex items-center gap-2.5 border px-5 py-3 ${statusStyle(tracked.status)}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.22em]">
                      {t.statuses[tracked.status] || tracked.status}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal>
            <div className="border border-border bg-[#101216] p-7 md:p-9">
              <p className="font-body font-light leading-relaxed text-foreground/85 mb-10">{t.s3Sub}</p>

              <form onSubmit={submit} className="space-y-10">
                <div>
                  <label className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-muted-foreground">
                    {t.fName}
                  </label>
                  <input
                    data-testid={KYC.nameInput}
                    className="flair-input mt-2"
                    placeholder={t.fNamePh}
                    value={form.name}
                    onChange={update('name')}
                  />
                </div>

                <div>
                  <label className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-muted-foreground">
                    {t.fRef}
                  </label>
                  <input
                    data-testid={KYC.refInput}
                    className="flair-input mt-2"
                    placeholder={t.fRefPh}
                    value={form.reference}
                    onChange={update('reference')}
                  />
                </div>

                <div>
                  <label className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-muted-foreground">
                    {t.fAddr}
                  </label>
                  <div className="mt-2 flex items-end gap-4">
                    <input
                      data-testid={KYC.addressInput}
                      className="flair-input font-mono"
                      placeholder={t.fAddrPh}
                      value={form.address}
                      onChange={update('address')}
                    />
                    <button
                      type="button"
                      data-testid={KYC.pasteButton}
                      onClick={pasteAddress}
                      className="shrink-0 flex items-center gap-2 border border-primary/40 px-5 py-3 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                    >
                      <ClipboardPaste size={14} strokeWidth={1.5} />
                      {t.paste}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Lock size={15} strokeWidth={1.5} className="text-muted-foreground mt-0.5 shrink-0" />
                  <p className="font-body font-light text-sm leading-relaxed text-muted-foreground">
                    {t.encrypt}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid={KYC.submitButton}
                  className="group w-full flex items-center justify-center gap-3 border border-primary py-5 font-mono text-xs uppercase tracking-[0.24em] text-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-400 disabled:opacity-60"
                >
                  {loading ? t.submitting : t.submit}
                  <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </form>
            </div>
          </Reveal>

          {/* Renew / Retry */}
          <Reveal>
            <div className="mt-8 border border-dashed border-border p-7 md:p-9">
              <h3 className="font-display font-light text-3xl tracking-tight text-foreground mb-3">
                {t.renewTitle}
              </h3>
              <p className="font-body font-light text-sm leading-relaxed text-muted-foreground mb-7 max-w-md">
                {t.renewSubtitle}
              </p>
              <button
                data-testid={KYC.renewButton}
                onClick={pasteRenew}
                disabled={renewLoading}
                className="w-full flex items-center justify-center gap-3 border border-primary py-5 font-mono text-xs uppercase tracking-[0.22em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300 disabled:opacity-60"
              >
                <ClipboardPaste size={16} strokeWidth={1.5} />
                {renewLoading ? t.submitting : t.renew}
              </button>
            </div>
          </Reveal>
        </section>
      </main>

      {/* Footer strip */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-screen-lg px-5 md:px-8 py-6 text-center">
          <Link
            to="/"
            data-testid={KYC.brandHome}
            className="font-mono text-xs tracking-[0.05em] text-foreground/80 hover:text-primary transition-colors duration-300"
          >
            {DOMAIN}
          </Link>
          <span className="font-mono text-xs tracking-[0.05em] text-muted-foreground"> — {t.private}</span>
        </div>
      </footer>
    </div>
  );
}
